import { SearchOutlined } from '@ant-design/icons';
import { Button, Drawer, Empty, Input, Skeleton, Space, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { pageSizeOptions } from '../../../constants/AppConstants';
import CloseIcon from '../../../assets/icons/close-icon.svg';
import { iconSelectorLandingZone } from '../../../pages/Dashboard/DashboardCover/BuildDeploy/BuildDeployConstants';
import { iconSelectorConfigureLandingZone } from '../../../constants/ModalConstants';
import { getFormattedTimestamp } from '../../../libs/helper/HelperFunctions';
import { LandingZoneStatus } from '../../../libs/models/BuildDeployModels';
import {
  ILandingZoneNotification,
  ILandingZoneNotificationReqPayload,
} from '../../../libs/models/NotificationModels';
import { NotificationActions } from '../../../libs/store/actions/NotificationActions';
import { useAppSelector } from '../../../libs/store/reducers';
import { NotificationDrawerLabels as LABELS } from './NotificationDrawerConstants';
import './NotificationDrawerStyles.less';

/* Component : Notification Drawer to Render Landing Zone Notification's in Drawer
props : none
*/

const NotificationDrawer = () => {
  const dispatch = useDispatch();

  const [searchText, setSearchText] = useState<string>('');

  const { landingZoneNotifications, selectedAccountId, selectedProjectId, loading } =
    useAppSelector(
      ({
        notification: { landingZoneNotifications, loading },
        overview: { selectedAccountId, selectedProjectId },
      }) => ({
        landingZoneNotifications,
        selectedAccountId,
        selectedProjectId,
        loading,
      }),
    );

  const dispatcher = {
    getLandingZoneNotifications: (data: ILandingZoneNotificationReqPayload) =>
      dispatch(NotificationActions.getLandingZoneNotificationStart(data)),
    showNotificationsDrawer: (data: boolean) =>
      dispatch(NotificationActions.toggleLandingZoneNotificationStart(data)),
    toggleNotificationDot: (data: boolean) =>
      dispatch(NotificationActions.toggleLandingZoneNotificationDot(data)),
  };

  useEffect(() => {
    dispatcher.toggleNotificationDot(false);
    if (selectedAccountId) {
      dispatcher.getLandingZoneNotifications({
        accountId: selectedAccountId || '',
        projectId: selectedProjectId || '',
      });
    }
  }, []);

  const notificationsList = useMemo(() => {
    return landingZoneNotifications.filter(({ landingZoneName }) => {
      return landingZoneName.toUpperCase().includes(searchText.toUpperCase());
    });
  }, [landingZoneNotifications, searchText]);

  //Callback Method to search the Notification's using name
  const onSearch = (searchText: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(searchText.target.value);
  };

  const projectTitle = () => (
    <div className="project-search-label">
      {
        <Input
          placeholder={'Search'}
          onChange={onSearch}
          prefix={<SearchOutlined className="project-search-icon" />}
        />
      }
      <div>{LABELS.environment}</div>
    </div>
  );

  const columns: ColumnsType<ILandingZoneNotification> = [
    {
      title: projectTitle(),
      dataIndex: LABELS.landingZoneName,
      key: LABELS.landingZoneName,
      className: 'ant-table-header-search',
      render: (value: string, record: ILandingZoneNotification) => {
        return (
          <div className="user-table-status-column">
            <div className="user-table-status-content">
              <div
                className={
                  record.status === LandingZoneStatus.IN_PROGRESS
                    ? 'status-circle inprogress-tag'
                    : 'status-circle completed-tag'
                }
              />
              <img
                src={`${process.env.PUBLIC_URL}/config-icons/${iconSelectorConfigureLandingZone(
                  record.cloudProvider.toLocaleLowerCase()!,
                )}`}
                className="user-table-status-icon"
                alt="cloud-icon"
              />
            </div>
            <Typography>{value}</Typography>
          </div>
        );
      },
    },
    {
      title: LABELS.account,
      dataIndex: LABELS.account.toLocaleLowerCase(),
      key: LABELS.account.toLocaleLowerCase(),
      //Sorter for sorting according alphabetical order
      sorter: (a: ILandingZoneNotification, b: ILandingZoneNotification) =>
        a.account.localeCompare(b.account),
    },
    {
      title: LABELS.project,
      dataIndex: LABELS.project.toLocaleLowerCase(),
      key: LABELS.project.toLocaleLowerCase(),
      //Sorter for sorting according alphabetical order
      sorter: (a: ILandingZoneNotification, b: ILandingZoneNotification) =>
        a.project.localeCompare(b.project),
    },
    {
      title: LABELS.username,
      dataIndex: LABELS.username.toLocaleLowerCase(),
      key: LABELS.username.toLocaleLowerCase(),
      //Sorter for sorting according alphabetical order
      sorter: (a: ILandingZoneNotification, b: ILandingZoneNotification) =>
        a.username.localeCompare(b.username),
    },
    {
      title: LABELS.dateTime,
      dataIndex: LABELS.dateTimeLabel,
      key: LABELS.dateTimeLabel,
      render: (createdOn: string) => {
        return {
          children: createdOn !== undefined && getFormattedTimestamp(createdOn),
        };
      },
      //Sorter to sort according to the notification time
      sorter: (a: ILandingZoneNotification, b: ILandingZoneNotification) =>
        new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime(),
      defaultSortOrder: 'descend',
    },
    {
      title: LABELS.status,
      dataIndex: LABELS.status.toLocaleLowerCase(),
      key: LABELS.status.toLocaleLowerCase(),
      render: (value: LandingZoneStatus, record: ILandingZoneNotification) => {
        return {
          children: (
            <div className="user-table-status-column">
              <Typography
                className={
                  value === LandingZoneStatus.IN_PROGRESS ||
                  value === LandingZoneStatus.DELETE_IN_PROFRESS
                    ? 'status-tag inprogress-tag'
                    : 'status-tag completed-tag'
                }
              >
                {value}
              </Typography>
            </div>
          ),
        };
      },
    },
  ];

  return (
    <Drawer
      className="notification-drawer"
      title={`Notifications (${landingZoneNotifications.length})`}
      placement="right"
      width="70rem"
      open={true}
      extra={
        <Space>
          <Button onClick={() => dispatcher.showNotificationsDrawer(false)} type="text">
            <img src={CloseIcon} alt="close-icon" className="notification-drawer__close__btn" />
          </Button>
        </Space>
      }
    >
      <Table
        dataSource={loading ? [] : notificationsList}
        columns={columns}
        className="table-style notification-table"
        locale={{
          emptyText: loading ? <Skeleton active={true} /> : <Empty />,
        }}
        rowKey={({ Id }) => Id}
        pagination={{
          position: ['bottomLeft'],
          showSizeChanger: true,
          pageSizeOptions,
        }}
      />
    </Drawer>
  );
};

export default NotificationDrawer;
